import './AlgoliaTable.css'
import React, { useMemo, useState, useEffect, useContext } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Container, List, ListItem, useBreakpointValue, Skeleton, Stack, Icon, IconButton, Input, InputGroup, InputLeftElement, useColorModeValue as mode } from "@chakra-ui/react";
import { createColumnHelper, ColumnDef, flexRender,
    getCoreRowModel,
    useReactTable, ColumnHelper, Row } from "@tanstack/react-table"
import App, { AppContext } from '../../App';
import { InstantSearch, useHits, useInstantSearch, SearchBox, Pagination, UseHitsProps, Hits, HitsPerPage } from 'react-instantsearch';
import { IDeleteConfirmationModalConfig } from '../modals/DeleteConfirmationModal';

export interface IAlgoliaTableHitsProps<T> extends UseHitsProps {
    getColumns(columnHelper: ColumnHelper<T>, setEdit: React.Dispatch<React.SetStateAction<T | undefined>>, deleteAction: (item: T) => void): ColumnDef<T, any>[];
    getCard(item: T, setEdit: React.Dispatch<React.SetStateAction<T | undefined>>, deleteAction: (item: T) => void): { onClick?(): void, value: string, key: string, content: React.ReactNode };
    onRowClick?(row: Row<T>): void;
    cellWidths?: string[];

    addState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    deleteAction(item: T, deleteItem: (item: T) => void): IDeleteConfirmationModalConfig;
    getAddDrawer(state: [boolean, React.Dispatch<React.SetStateAction<boolean>>], addItem: (item: T) => void): React.ReactNode;
    getEditDrawer(state: [T | undefined, React.Dispatch<React.SetStateAction<T | undefined>>], replaceItem: (item: T) => void): React.ReactNode;
}

export const AlgoliaTableHits = <T,>(props: IAlgoliaTableHitsProps<T>) => {
    const isDesktop = useBreakpointValue({ base: false, lg: true }, { ssr: false });
    const boxShadow = mode('sm', 'sm-dark');

    const { setDCM } = useContext(AppContext);
    const { refresh } = useInstantSearch();

    const { hits } : { hits: any[] } = useHits();
    const [data, setData] = useState<T[]>([]);
    const addItem = (item: any) => {
        setData(old => ([item, ...old]));
        setTimeout(refresh, 5000);
    }
    const replaceItem = (item: any) => {
        setData(old => {
            var items = [...old];
            var index = items.findIndex((t: any) => t.id === item.id);
            items.splice(index, 1, item);
            return items;
        });
        setTimeout(refresh, 5000);
    }
    const deleteItem = (item: any) => {
        setData(old => {
            var items = [...old];
            var index = items.findIndex((t: any) => t.id === item.id);
            items.splice(index, 1);
            return items;
        });
        setTimeout(refresh, 5000);
    }

    const deleteAction = (item: any) => {
        var config = props.deleteAction(item, deleteItem);
        setDCM({...config});
    }

    const [add, setAdd] = props.addState;
    const [edit, setEdit] = useState<T | undefined>(undefined);

    useEffect(() => {
        setData([...hits]);
    }, [hits]);

    /*
    useEffect(() => {
        const intervalId = setInterval(refresh, 10000);
        return () => clearInterval(intervalId);
    }, []);
    */

    const { getColumns, getCard, getAddDrawer, getEditDrawer } = props;
    const columnHelper = useMemo(() => createColumnHelper<T>(), []);
    const columns = useMemo(() => getColumns(columnHelper, setEdit, deleteAction), [getColumns, columnHelper]);
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnResizing: props.cellWidths ? false : true,
        columnResizeMode: 'onChange',
    });

    return (
        <>
        {((isDesktop && (
            <Container 
                h="full" 
                px="0" 
                py="0" 
                overflowY="auto"
            >
                <Table 
                    w="full"
                >
                    <Thead 
                        style={{ 
                            position: 'sticky', 
                            top: '0', 
                            //zIndex: 4
                        }
                    }>
                        {table.getHeaderGroups().map(headerGroup => (
                            <Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header, headerIndex) => {
                                return (
                                    <Th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={{ 
                                            position: 'relative', 
                                            width: (props.cellWidths && props.cellWidths.length > headerIndex) ? props.cellWidths[headerIndex] : header.getSize(), 
                                        }}
                                    >
                                    {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                        )}
                                    {header.column.getCanResize() && (
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={`resizer ${
                                            header.column.getIsResizing() ? 'isResizing' : ''
                                            }`}
                                        ></div>
                                    )}
                                    </Th>
                                )
                            })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                    {table.getRowModel().rows.map(row => {
                        return (
                            <Tr key={row.id} onClick={() => props.onRowClick && props.onRowClick(row)} _hover={props.onRowClick ? { cursor: 'pointer' } : undefined}>
                            {row.getVisibleCells().map((cell, cellIndex) => {
                                return (
                                    <Td key={cell.id} style={{ 
                                        width: (props.cellWidths && props.cellWidths.length > cellIndex) ? props.cellWidths[cellIndex] : cell.column.getSize(), 
                                        whiteSpace: 'normal', 
                                        overflowWrap: 'anywhere' 
                                    }}>
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                    </Td>
                                )
                            })}
                            </Tr>
                        )
                    })}
                    </Tbody>
                </Table>
            </Container>
        )) || (
            <List listStyleType="none" spacing={{ base: "2", lg: "4" }} overflowY="auto" mt="2 !important">
            {data.map(p => {
                const { onClick, key, value, content } = getCard(p, setEdit, deleteAction);
                return (
                    <ListItem
                        key={key}
                        value={value}
                        bg="bg-surface"
                        p="4"
                        my="2"
                        mx="2"
                        boxShadow={boxShadow}
                        position="relative"
                        borderRadius="lg"
                        cursor={onClick ? "pointer" : undefined}
                        onClick={onClick}
                    >
                        {content}
                    </ListItem>
                )
            })}
            </List>
        ))}
        {getAddDrawer([add, setAdd], addItem)}
        {getEditDrawer([edit, setEdit], replaceItem)}
        </>
    )
}

export interface IAlgoliaTableProps<T> extends IAlgoliaTableHitsProps<T> {
    indexName: string;
}

export const AlgoliaTable = <T,>(props: IAlgoliaTableProps<T>) => {
    const { searchClient } = useContext(AppContext);
    return (
        <InstantSearch searchClient={searchClient} indexName={props.indexName} future={{ preserveSharedStateOnUnmount: true }}>
            <SearchBox />
            <AlgoliaTableHits {...props} />
            <Pagination />
            <HitsPerPage style={{ display: 'none' }}
                items={[
                    { label: '12', value: 12, default: true }
                ]}
            />
        </InstantSearch>
    );
}